/* eslint-disable react/display-name */
/* eslint-disable @babel/new-cap */
// eslint-disable no-unnecessary-condition
import React, {useState} from "react";
import {ColumnProps} from "antd/lib/table";
import {grey} from "@ant-design/colors";
import css from "../../../views/TableColumnRenderers/TableColumnRenderers.less";
import {Typography} from "antd";
import {
    CourseLesson,
    ArrayDataOptions,
    PaymentInterval,
    Maybe,
    PaymentTransferType,
    CourseLessonBooking,
    GetBookingsOverviewOptimizedQuery,
    PaymentProvider,
    useGetAppSettingsQuery,
    useGetDiscountCodeFilterOptionsQuery,
} from "../../../generated/graphql";
import {DISCOUNT_CODE_FILTER_NONE} from "../../../../shared/const/discountCodeFilter";
import {useDiscountCodesAllowed} from "../../../helpers/useDiscountCodesAllowed";
import {PaymentMethodFilterCategory} from "../../../../shared/models/paymentMethodFilterCategory";
import {
    renderAdditionalInfo,
    RenderAmount,
    renderAmount,
    renderAttendeeFullName,
    renderBirthday,
    renderBirthdayAsAge,
    RenderBookedLessons,
    // renderBookedLessons,
    renderBookerFullName,
    renderBookingNumber,
    RenderBookingPrice,
    renderBookingType,
    renderBooleanAsCheckmark,
    renderContinuousText,
    renderCourseLessons,
    renderCourseNumber,
    renderCourseType,
    renderDate,
    // renderEmailNotifications,
    renderFirstCourseLesson,
    renderFullName,
    renderInstructors,
    renderLastCourseLesson,
    renderPaymentDate,
    renderPaymentType,
    renderPercent,
    renderTextStandard,
    renderTimeFromNow,
    renderUnknown,
} from "../../../views/TableColumnRenderers/TableColumnRenderers";
import PaymentMethodBadge from "../../../views/PaymentMethodBadge/PaymentMethodBadge";
import i18n from "../../../services/i18n";
import {isDefined} from "../../../helpers/typeScriptHelpers";
import {isBookingSettled} from "../../../helpers/bookingDetailsRouting";
import columnSortFilterTest, {InputType} from "../../../helpers/columnSortFilterTest";
import RenderInvoicePreview from "../../../views/RenderInvoicePreview/RenderInvoicePreview";
import useHighestRole from "../../../helpers/useHighestRole";
import {useCurrentUserRoles} from "../../../helpers/useIsStaffOnlyAccess";
import useTransferWizardMode from "../../../hooks/useTransferWizardMode";
import {useStaffVisibility} from "../../../hooks/useStaffVisibility";
import {StaffVisibilityLevel} from "../../../../shared/models/staffVisibility";
import {renderEmailNotifications} from "../../../views/TableColumnRenderers/renderEmailNotifications";
import RenderActions, {RenderActionsProps} from "./ColumnActions/RenderActions";
import {
    URL_BOOKING_EDIT_ID,
    URL_BOOKINGS_SETTLEMENT_DETAILS,
    URL_INSTRUCTOR_BOOKING_EDIT_ID,
    URL_INSTRUCTOR_INVOICE_SETTLEMENT_DETAILS,
    URL_LESSON_BOOKING_EDIT_ID,
    URL_LESSON_BOOKINGS_SETTLED_DETAILS,
} from "../../../models/url";
import Roles from "../../../../shared/models/roles";
import LateEntryPricingNotice from "../../../views/LateEntryPricingNotice/LateEntryPricingNotice";
import {getDisplayedPaymentMethodType} from "../../../helpers/getDisplayedPaymentMethodType";
// import RenderActions from "./ColumnActions/RenderActions";

export type EditParamsType = {bookingId: string} & Pick<
    TableType,
    "attendee" | "coursePriceId" | "coursePrice" | "courseLessons" | "bookingNumberPrefix" | "noticeStaff" | "paymentType" | "isSpecial" | "grossPrice" | "netPrice" | "vat"
>;

type ExtraColumns = {
    rowIndex: "rowIndex";
    actions: "actions";
    courseType: "courseType";
    prefixedCourseNumber: "prefixedCourseNumber";
    booker: "booker";
    bookerFullName: "bookerFullName";
    bookerLastFirst: "bookerLastFirst";
    attendeeFullName: "attendeeFullName";
    attendeeLastFirst: "attendeeLastFirst";
    city: "city";
    location: "location";
    firstCourseLesson: "firstCourseLesson";
    lastCourseLesson: "lastCourseLesson";
    billed: "billed";
    invoiceingActions: "invoiceingActions";
    invoiceNumber: "invoiceNumber";
    settledActions: "settledActions";
    nonDisclosureNotice: "nonDisclosureNotice";
    phoneNumber: "phoneNumber";
    mobileNumber: "mobileNumber";
    email: "email";
    birthday: "birthday";
    age: "age";
    dateOfBooking: "dateOfBooking";
    emailNotifications: "emailNotifications";
    // startDateTimeBooking: "startDateTimeBooking";
    credit: "credit";
    instructors: "instructors";
    lessonCount: "lessonCount";
    bookedLessons: "bookedLessons";
    attendeeMember: "attendeeMember";
    discountCode: "discountCode";
};

export type TableType = {
    dataIndex?: string;
    // PERFORMANCE OPTIMIZATION: Course direkt auf Booking für Light Query
    course?: {
        id: string;
        prefixedCourseNumber: string;
        allowLessonBooking: boolean;
        lessonCount: number;
        paymentInterval?: PaymentInterval;
        freePlaces?: number;
        maxAttendees?: number;
        courseType: {
            id: string;
            name: string;
            color: string;
        };
        location: {
            id: string;
            city: string;
            name: string;
        };
        instructors: Array<{
            id: string;
            firstname: string;
            lastname: string;
        }>;
    };
} & GetBookingsOverviewOptimizedQuery["bookings"]["items"][number] &
    ExtraColumns;

// Namensspalten gibt es in zwei Reihenfolgen: Titel ist die kurze Rolle, die Reihenfolge
// (= Sortierung) steht als kleine graue Unterzeile darunter, damit der Kopf nicht umbricht.
const NAME_COLUMN_HEADERS: Partial<Record<string, {role: "booker" | "attendee"; order: "nameOrderFirstLast" | "nameOrderLastFirst"}>> = {
    bookerFullName: {role: "booker", order: "nameOrderFirstLast"},
    bookerLastFirst: {role: "booker", order: "nameOrderLastFirst"},
    attendeeFullName: {role: "attendee", order: "nameOrderFirstLast"},
    attendeeLastFirst: {role: "attendee", order: "nameOrderLastFirst"},
};
const NAME_COLUMN_WIDTH = 170;

const renderNameColumnHeader = (role: string, order: string) => (
    <span>
        {role}
        <span style={{display: "block", fontSize: "0.8em", fontWeight: "normal", color: grey[2], whiteSpace: "nowrap"}}>{order}</span>
    </span>
);

export const rowKey: (course: TableType) => TableType["id"] = (booking) => booking.id;

export const useHandleColumns = (
    columnsToShow: Array<Extract<keyof TableType, string>>,
    editLink: string,
    detailsLink: string,
    options: Maybe<ArrayDataOptions> | undefined,
    setOptions: (options: ArrayDataOptions) => void,
    actionColumn?: Function,
    tableData?: Array<TableType>,
    activeFilters?: ArrayDataOptions["filter"],
    selectedRowKeys?: Array<string>,
    hideFiltersAndSorters?: boolean,
    courseDetailsLink?: string,
    refetch?: () => void,
    // eslint-disable-next-line max-params
) => {
    const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
    const shownColumns: Array<ColumnProps<TableType>> = [];
    const highestRole = useHighestRole();
    // #809: Rollen-Gates brauchen den vollen Rollensatz (Assistant/BetaTester fallen bei
    // `useHighestRole()` auf `EndUser` zusammen). Einmal pro Liste geholt, pro Zeile gereicht.
    const {roles: currentUserRoles} = useCurrentUserRoles();
    // Umbuchungs-Engine (#660, Teil 2c): einmal pro Liste ausgewertet und an jede Zeile
    // durchgereicht — ein Hook je Tabellenzeile waere unnoetige Apollo-Last.
    const {wizardMode: transferWizardMode} = useTransferWizardMode();
    // Sichtbarkeitsregel „Buchungsaktionen“ (#1188), ebenfalls einmal pro Liste.
    const bookingActionsDisabled = useStaffVisibility("bookingActions") === StaffVisibilityLevel.NoAccess;

    // Strict === true (kein Default-true, kein Loading-Guard), analog StepCoursePrice.tsx:58.
    // Bei aktivem Mollie erhaelt der Bezahlmethode-Filter zusaetzliche Optionen.
    const {data: appSettingsData} = useGetAppSettingsQuery();
    const featureMollie = appSettingsData?.settings?.featureMollie === true;

    // Rabattcodes (Beta): Filteroptionen nur laden, wenn die Spalte zu sehen ist (Query ist ab Admin freigegeben).
    const discountCodesAllowed = useDiscountCodesAllowed();
    const {data: discountCodeOptionsData} = useGetDiscountCodeFilterOptionsQuery({
        skip: !discountCodesAllowed || !columnsToShow.includes("discountCode"),
        fetchPolicy: "cache-and-network",
    });

    const getColumnsToShow = (columns: Array<{dataIndex: string} & TableType>) => {
        columns.forEach((col) => {
            if (columnsToShow.some((value) => value === col.dataIndex)) {
                shownColumns.push(col);
            }
        });

        moveLeftAligndColumnToFirstPosition(shownColumns);
        moveActionColumnToLastPosition(shownColumns);
        moveRowIndexToFirstPosition(shownColumns);
    };

    const moveActionColumnToLastPosition = (shownColumns: Array<ColumnProps<TableType>>) => {
        shownColumns.forEach((col) => {
            if (col.fixed === "right") {
                shownColumns.push(shownColumns.splice(shownColumns.indexOf(col), 1)[0]);
            }
        });
    };

    const moveLeftAligndColumnToFirstPosition = (shownColumns: Array<ColumnProps<TableType>>) => {
        // Sammle alle left-aligned Spalten in ihrer ursprünglichen Reihenfolge
        const leftAlignedColumns: Array<ColumnProps<TableType>> = [];

        // Iteriere rückwärts, um die Indizes beim Entfernen nicht zu verschieben
        for (let i = shownColumns.length - 1; i >= 0; i--) {
            const col = shownColumns[i];

            if (col.fixed === "left") {
                // Entferne die Spalte und füge sie am Anfang der leftAlignedColumns hinzu
                leftAlignedColumns.unshift(shownColumns.splice(i, 1)[0]);
            }
        }

        // Füge alle left-aligned Spalten in der richtigen Reihenfolge am Anfang ein
        shownColumns.unshift(...leftAlignedColumns);
    };

    const moveRowIndexToFirstPosition = (shownColumns: Array<ColumnProps<TableType>>) => {
        shownColumns.forEach((col) => {
            if (col.dataIndex === "rowIndex") {
                shownColumns.unshift(shownColumns.splice(shownColumns.indexOf(col), 1)[0]);
            }
        });
    };

    // const columns: Array<TableType> = [];
    const columns: Array<{dataIndex: string} & TableType> = [];

    const column: ColumnProps<TableType> = {
        title: "",
        fixed: false,
    };

    columnsToShow.forEach((col: Extract<keyof TableType, string>) => {
        const c = {...column};

        const nameHeader = NAME_COLUMN_HEADERS[col];
        // Datenfelder ohne Spaltentitel (course, courseLessonBookings) landen nie in columnsToShow
        const colTitle = i18n.views.TableColumnTitles[(nameHeader?.role ?? col) as Extract<typeof col, keyof typeof i18n.views.TableColumnTitles>]();

        const rawTitle = colTitle;
        // Nur in einen pre-line-span wrappen, wenn der Titel tatsaechlich umgebrochen werden kann (enthaelt ein Leerzeichen).
        // Andernfalls blockiert der Block-span das th-Layout und verdraengt Sort-/Filter-Icons (z.B. bei "Buchungsdatum").
        const title = rawTitle.length <= 12 || !rawTitle.includes(" ") ? rawTitle : <span style={{whiteSpace: "pre-line"}}>{rawTitle.replace(" ", "\n")}</span>;

        const standardEllipsis = false;

        c.ellipsis = standardEllipsis;
        c.title = nameHeader ? renderNameColumnHeader(colTitle, i18n.views.TableColumnTitles[nameHeader.order]()) : title;
        c.dataIndex = col;
        c.width = nameHeader ? NAME_COLUMN_WIDTH : 100 + colTitle.length * 2;

        let path = c.dataIndex;

        const sortFilter = ({
            dataIndex,
            disabled,
            type,
            selectOptions,
        }: {
            dataIndex: string;
            disabled?: boolean;
            type: InputType;
            selectOptions?: Array<{
                label: string;
                value: string;
            }>;
            activeFilters?: ArrayDataOptions["filter"] | undefined;
        }) =>
            // eslint-disable-next-line max-params
            {
                if (hideFiltersAndSorters) {
                    return null;
                }

                Object.entries(
                    columnSortFilterTest({
                        dataIndex,
                        type,
                        selectOptions,
                        options,
                        setOptions,
                        dropdownOpen,
                        setDropdownOpen,
                        table: "booking", // Explizit: Korrekte Tabelle für Bookings
                    }),
                ).map(([key, value]) => {
                    if (key !== "filteredValue") {
                        // @ts-ignore
                        return (c[key] = disabled ? null : value);
                    }

                    return null;
                });
            };

        switch (col) {
            case "rowIndex":
                c.fixed = "left";
                c.width = "6px";
                c.className = css.rowIndex;
                c.align = "center";
                c.render = (text, record: TableType, index: number) => index + 1;
                break;
            case "actions":
                c.fixed = "right";
                c.width = 10;
                c.align = "center";
                if (actionColumn) {
                    // c.render = (text, record: TableType) => ({
                    //     displayName: "Aktionen",
                    //     children: (
                    //         <RenderActions
                    //             record={record}
                    //             editLink={editLink}
                    //             detailsLink={detailsLink}
                    //             actionColumn={actionColumn}
                    //             // selectedRowKeys={selectedRowKeys}
                    //             refetch={refetch}
                    //         />
                    //     ),
                    // });
                    c.render = (text, record: TableType) =>
                        renderActions({
                            record,
                            // editLink,
                            editLink: record.course?.allowLessonBooking ? URL_LESSON_BOOKING_EDIT_ID : URL_BOOKING_EDIT_ID,
                            // detailsLink,
                            detailsLink: record.course?.allowLessonBooking ? URL_LESSON_BOOKINGS_SETTLED_DETAILS : URL_BOOKINGS_SETTLEMENT_DETAILS,
                            actionColumn,
                            // selectedRowKeys,
                            refetch,
                            highestRole,
                            roles: currentUserRoles,
                            transferWizardMode,
                            bookingActionsDisabled,
                        });
                } else {
                    // c.render = (text, record: TableType) => ({
                    //     displayName: "Aktionen",
                    //     children: (
                    //         <RenderActions
                    //             record={record}
                    //             editLink={editLink}
                    //             detailsLink={detailsLink}
                    //             // actionColumn={actionColumn}
                    //             // selectedRowKeys={selectedRowKeys}
                    //             refetch={refetch}
                    //         />
                    //     ),
                    // });
                    c.render = (text, record: TableType) =>
                        renderActions({
                            record,
                            // editLink,
                            editLink: record.course?.allowLessonBooking ? URL_LESSON_BOOKING_EDIT_ID : URL_BOOKING_EDIT_ID,
                            // detailsLink,
                            detailsLink: record.course?.allowLessonBooking ? URL_LESSON_BOOKINGS_SETTLED_DETAILS : URL_BOOKINGS_SETTLEMENT_DETAILS,
                            // actionColumn
                            // selectedRowKeys
                            refetch,
                            highestRole,
                            roles: currentUserRoles,
                            transferWizardMode,
                            bookingActionsDisabled,
                        });
                }
                break;
            case "invoiceingActions":
                if (actionColumn) {
                    c.fixed = "right";
                    c.width = 10;
                    c.align = "center";
                    c.render = (text, record: TableType) => actionColumn(record);
                }
                break;
            case "settledActions":
                if (actionColumn) {
                    c.title = null;
                    c.fixed = "right";
                    c.width = 10;
                    c.align = "center";
                    c.render = (text, record: TableType) => actionColumn(record);
                }
                break;
            case "bookingNumber":
                c.fixed = "left";
                c.width = 145;
                c.render = (_, record: TableType) => {
                    const searchKey = options?.filter?.find((filter) => filter.column === "bookingNumber")?.values[0];
                    // Prüfe ob mindestens eine ScheduledPayment über Mollie läuft
                    const hasMolliePayment = record.scheduledPayments?.some((sp) => sp.paymentProvider === PaymentProvider.Mollie) ?? false;

                    return {
                        children: (
                            <div>
                                {renderBookingNumber({
                                    bookingNumberPrefix: record.bookingNumberPrefix,
                                    bookingNumber: record.bookingNumber,
                                    bookingType: record.bookingType,
                                    className: css.primary,
                                    canceled: Boolean(record.canceled),
                                    bookingId: record.id,
                                    isSettled: isBookingSettled(record),
                                    highestRole,
                                    searchKey,
                                    allowLessonBooking: Boolean(record.course?.allowLessonBooking),
                                    hasMolliePayment,
                                })}
                                <div style={{marginTop: 4}}>
                                    <LateEntryPricingNotice compact pricingReason={record.pricingReason} />
                                </div>
                            </div>
                        ),
                    };
                };
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                break;
            case "courseType":
                path = "course.courseType.name";
                c.render = (text, record: TableType) =>
                    renderCourseType({
                        name: record.course?.courseType.name ?? "",
                        color: record.course?.courseType.color,
                    });
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                break;
            case "prefixedCourseNumber":
                c.render = (text, record: TableType) =>
                    renderCourseNumber({
                        courseNumber: record.course?.prefixedCourseNumber ?? undefined,
                        courseId: record.course?.id ?? "",
                        detailsLink: courseDetailsLink,
                    });
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    // Multi-Value-Tag-Input: mehrere Kursnummern gleichzeitig
                    // filtern. Backend (booking.addFilterToQuery -> precise
                    // prefixedCourseNumber-Matching) OR-verknuepft bereits das
                    // ganze filter.values-Array.
                    type: "searchMultiple",
                });
                // c.sorter = false;
                break;
            case "bookerFullName":
                c.render = (text: unknown, record: TableType) =>
                    renderBookerFullName(
                        record.attendee?.booker?.id,
                        record.attendee?.booker?.firstname,
                        record.attendee?.booker?.lastname,
                        css.primary,
                        record.attendee?.booker?.nonDisclosureNotice ?? "",
                    );
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                // c.sorter = false;
                break;
            case "bookerLastFirst":
                c.render = (text: unknown, record: TableType) =>
                    renderBookerFullName(
                        record.attendee?.booker?.id,
                        record.attendee?.booker?.firstname,
                        record.attendee?.booker?.lastname,
                        css.primary,
                        record.attendee?.booker?.nonDisclosureNotice ?? "",
                        true,
                    );
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                break;
            case "booker":
                c.render = (text: unknown, record: TableType) =>
                    renderBookerFullName(
                        record.attendee?.booker?.id,
                        record.attendee?.booker?.firstname,
                        record.attendee?.booker?.lastname,
                        css.primary,
                        record.attendee?.booker?.nonDisclosureNotice ?? "",
                    );
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: true,
                    type: "search",
                });
                c.sorter = false;
                break;
            case "phoneNumber":
                c.render = (text: unknown, record: TableType) => renderTextStandard(record.attendee?.booker?.phoneNumber);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                c.sorter = false;
                break;
            case "mobileNumber":
                c.render = (text: unknown, record: TableType) => renderTextStandard(record.attendee?.booker?.mobileNumber);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                c.sorter = false;
                break;
            case "email":
                c.render = (text: unknown, record: TableType) => renderTextStandard(record.attendee?.booker?.email);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                // c.sorter = false;
                break;
            case "attendeeFullName":
                path = "attendee.lastname";
                c.render = (text, record: TableType) => renderAttendeeFullName(record.attendee?.booker?.id, record.attendee?.firstname, record.attendee?.lastname);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                // c.sorter = false;
                break;
            case "attendeeLastFirst":
                c.render = (text, record: TableType) => renderAttendeeFullName(record.attendee?.booker?.id, record.attendee?.firstname, record.attendee?.lastname, undefined, undefined, true);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                break;
            case "attendee":
                path = "attendee.lastname";
                c.render = (text, record: TableType) => renderFullName(record.attendee?.firstname, record.attendee?.lastname);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: true,
                    type: "search",
                });
                c.sorter = false;
                break;
            case "birthday":
                c.render = (text, record: TableType) => renderBirthday(record.attendee?.birthday);
                c.width = 130;
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "date",
                });
                c.sorter = true;
                break;
            case "age":
                c.render = (text, record: TableType) => renderBirthdayAsAge(record.attendee?.birthday);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "ageRange",
                });
                c.sorter = true;
                break;
            case "city":
                path = "course.location.city";
                c.render = (text, record: TableType) => renderTextStandard(record.course?.location.city);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                // c.sorter = false;
                break;
            case "location":
                path = "course.location.name";
                c.render = (text, record: TableType) => renderTextStandard(record.course?.location.name);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                // c.sorter = false;
                break;
            case "firstCourseLesson":
                path = "startDateTime";
                // Zeigt den Kursbeginn, auch bei Späteinsteigern — Anbieter lassen
                // versäumte Einheiten nachholen und brauchen den echten Start.
                // Sortierung und Filter der Spalte meinen denselben Wert.
                c.render = (text, record: TableType) => {
                    // Die Kursdetails (GetBookingsByCourseRefactored) laden den Kurs nur an den Lektionen.
                    const course: {startDateTime?: string; endDateTime?: string; endDateTimeFirstCourseLesson?: string} | undefined =
                        record.course ?? (record.courseLessons?.[0] as {course?: {startDateTime?: string; endDateTimeFirstCourseLesson?: string}} | undefined)?.course;
                    // Ende der ERSTEN Einheit; course.endDateTime ist das Ende der letzten.
                    const endDateTime = course?.endDateTimeFirstCourseLesson ?? course?.endDateTime;

                    return renderFirstCourseLesson({
                        startEnd: course?.startDateTime && endDateTime ? {startDateTime: course.startDateTime, endDateTime} : undefined,
                    });
                };
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "date",
                });
                c.sorter = true;
                break;
            case "lastCourseLesson":
                // path = "lastCourseLesson";
                c.render = (text, record: TableType) =>
                    renderLastCourseLesson({
                        courseLessons: record.courseLessons as Array<CourseLesson>,
                    });
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "date",
                });
                break;
            case "coursePrice": // This is representing the price of the course
                path = "coursePrice";
                c.render = (text, record: TableType) => renderAmount(record.coursePrice?.grossPrice);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: true,
                    type: "number",
                });
                c.sorter = false;
                break;
            case "grossPrice": // This is showing the price of the booking
                // c.title = i18n.views.TableColumnTitles.coursePrice();
                c.width = 80;
                c.render = (text, record: TableType) => ({
                    displayName: "Brutto",
                    children: <RenderBookingPrice record={record} />,
                });
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: true,
                    type: "number",
                });
                c.sorter = false;
                break;
            case "netPrice":
                c.render = (text, record: TableType) => ({
                    displayName: "Netto",
                    children: <RenderAmount amount={record.netPrice ?? 0} />,
                });
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: true,
                    type: "number",
                });
                c.sorter = false;
                break;
            case "vat":
                c.render = (text, record: TableType) => renderPercent(record.vat);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: true,
                    type: "number",
                });
                c.sorter = false;
                break;
            case "billed":
                c.align = "center";
                c.render = (text, record: TableType) => renderBooleanAsCheckmark(isBookingSettled(record));
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: true,
                    type: "boolean",
                });
                c.sorter = false;
                break;
            case "updateDate":
                c.render = (text, record: TableType) => renderTimeFromNow({date: record.updateDate, short: true});
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: true,
                    type: "date",
                });
                c.sorter = true;
                break;
            case "createDate":
                c.render = (text, record: TableType) => renderTimeFromNow({date: record.createDate, short: true});
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: true,
                    type: "date",
                });
                c.sorter = false;
                break;
            case "bookedBy":
                c.align = "center";
                c.width = 100;
                c.render = (text, record: TableType) => renderBooleanAsCheckmark(record.bookedBy === "Booker");

                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "select",
                    selectOptions: [
                        {
                            label: "Mitarbeiter",
                            value: "Staff",
                        },
                        {
                            label: "Online",
                            value: "Booker",
                        },
                    ],
                });
                c.sorter = false;
                break;
            case "noticeStaff":
                c.render = (text, record: TableType) => renderContinuousText(record.noticeStaff, 200, undefined, options?.filter?.find((filter) => filter.column === c.dataIndex)?.values[0]);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                // c.sorter = false;
                break;
            case "payDate":
                c.align = "center";
                c.render = (text, record: TableType) => renderPaymentDate(record.payDate);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "date",
                });
                c.sorter = true;
                break;
            case "bookingType":
                c.align = "center";
                c.render = (text, record: TableType) => renderBookingType(record.bookingType);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "select",
                    selectOptions: [
                        {
                            label: i18n.containers.bookings.BookingList.bookingTypes.Booking(),
                            value: "Booking",
                        },
                        {
                            label: i18n.containers.bookings.BookingList.bookingTypes.Waitlist(),
                            value: "Waitlist",
                        },
                        {
                            label: i18n.containers.bookings.BookingList.bookingTypes.PlaceReservation(),
                            value: "PlaceReservation",
                        },
                    ],
                });
                c.sorter = true;
                break;
            case "invoice":
                path = "invoice.invoiceNumber";
                c.align = "center";
                // eslint-disable-next-line react/display-name
                c.render = (text, record: TableType) => {
                    const getInvoiceData = (record: TableType) => {
                        if (!record.invoice) {
                            return undefined;
                        }
                        if (record.reversalInvoice) {
                            const {invoiceNumberPrefix, invoiceNumber, id} = record.reversalInvoice;

                            return {
                                id,
                                invoiceNumber,
                                invoiceNumberPrefix,
                                // canceled: Boolean(record.canceled !== null),
                                isReversalInvoice: true,
                            };
                        }

                        const {invoiceNumberPrefix, invoiceNumber, id} = record.invoice;

                        return {
                            id,
                            invoiceNumber,
                            invoiceNumberPrefix,
                            // canceled: Boolean(record.canceled !== null),
                            isReversalInvoice: false,
                        };
                    };

                    const invoiceData = getInvoiceData(record);

                    if (invoiceData === undefined) {
                        return <div>{"-"}</div>;
                    }

                    return <RenderInvoicePreview invoiceData={invoiceData} />;
                };
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                c.sorter = true;
                break;
            case "invoiceNumber":
                path = "invoice.invoiceNumber";
                c.align = "center";
                c.render = (text, record: TableType) =>
                    renderTextStandard(isDefined(record.invoice?.invoiceNumber) ? `${record.invoice?.invoiceNumberPrefix} - ${record.invoice?.invoiceNumber}` : "-");
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                c.sorter = true;
                break;
            case "paymentType":
                c.align = "center";
                c.render = (text, record: TableType) => renderPaymentType(record.paymentType);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "select",
                    selectOptions: [
                        {
                            label: "Vorkasse",
                            value: "PrePaid",
                        },
                        {
                            label: "Monatlich",
                            value: "Monthly",
                        },
                        {
                            label: "Individuell",
                            value: "Custom",
                        },
                        // {
                        //     label: "Vor Ort",
                        //     value: "OnTheSpot",
                        // },
                        // {
                        //     label: "Rechnung",
                        //     value: "Bill",
                        // },
                    ],
                });
                c.sorter = true;
                break;
            case "courseLessons":
                c.render = (text, record: TableType) => renderCourseLessons(record.course?.id ?? "");
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: true,
                    type: "search",
                });
                c.sorter = false;
                break;
            case "isSpecial":
                c.align = "center";
                c.render = (text, record: TableType) => {
                    // In Light Query haben wir keine courseLessons, nutze lessonCount vom course
                    const courseLessonCount = record.course?.lessonCount ?? 0;
                    const coursePrice = record.coursePrice?.grossPrice ?? 0;

                    return renderBooleanAsCheckmark(
                        Boolean(
                            // record.isSpecial ||
                            record.grossPrice !== coursePrice,
                        ),
                    );
                };
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: true,
                    type: "boolean",
                });
                c.sorter = false;
                break;
            case "emailNotifications":
                c.width = 160;
                c.render = (text, record: TableType) => renderEmailNotifications(record.emailNotifications);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "selectMultiple",
                    selectOptions: [
                        {
                            label: "Buchungsbestätigung",
                            value: "BookingConfirmation",
                        },
                        {
                            label: "Wartelistenbestätigung",
                            value: "WaitingListConfirmation",
                        },
                        {
                            label: "Rechnung",
                            value: "SettlementConfirmation",
                        },
                        {
                            label: "Stornierung",
                            value: "SettledBookingCancellation",
                        },
                    ],
                });
                c.sorter = false;
                break;
            case "bookingConfirmationSent":
                c.width = 160;
                c.align = "center";
                c.render = (text, record: TableType) => renderBooleanAsCheckmark(Boolean(record.bookingConfirmationSent));
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "select",
                    selectOptions: [
                        {
                            label: "Versendet",
                            value: "true",
                        },
                        {
                            label: "Nicht versendet",
                            value: "false",
                        },
                    ],
                });
                c.sorter = false;
                break;
            case "waitingListConfirmationSent":
                c.width = 170;
                c.align = "center";
                c.render = (text, record: TableType) => renderBooleanAsCheckmark(Boolean(record.waitingListConfirmationSent));
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "select",
                    selectOptions: [
                        {
                            label: "Versendet",
                            value: "true",
                        },
                        {
                            label: "Nicht versendet",
                            value: "false",
                        },
                    ],
                });
                c.sorter = false;
                break;
            case "invoiceSent":
                c.width = 100;
                c.align = "center";
                c.render = (text, record: TableType) => renderBooleanAsCheckmark(Boolean(record.invoiceSent));
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "select",
                    selectOptions: [
                        {
                            label: "Versendet",
                            value: "true",
                        },
                        {
                            label: "Nicht versendet",
                            value: "false",
                        },
                    ],
                });
                c.sorter = false;
                break;
            case "cancelationSent":
                c.width = 180;
                c.align = "center";
                c.render = (text, record: TableType) => renderBooleanAsCheckmark(Boolean(record.cancelationSent));
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "select",
                    selectOptions: [
                        {
                            label: "Versendet",
                            value: "true",
                        },
                        {
                            label: "Nicht versendet",
                            value: "false",
                        },
                    ],
                });
                c.sorter = false;
                break;
            case "nonDisclosureNotice":
                c.render = (text, record: TableType) => renderContinuousText(record.attendee?.booker?.nonDisclosureNotice ?? "-");
                // Bewusst ohne sortFilter(): gewünscht war Sortierung, ein zusätzliches
                // Filter-Dropdown war nicht gefragt. c.sorter allein genügt dafür.
                c.sorter = true;
                break;
            case "foundUs":
                c.render = (text, record: TableType) => renderContinuousText(record.foundUs ?? "-");
                break;
            case "utmSource":
                // Herkunft aus dem Link (`utm_source`), z. B. „newsletter“; nur bei Online-Buchungen gesetzt.
                c.render = (text, record: TableType) => record.utmSource ?? "-";
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                c.sorter = false;
                break;
            case "dateOfBooking":
                c.width = 140;
                c.render = (text, record: TableType) => renderDate(record.createDate, true);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "date",
                });
                c.sorter = true; // THIS DOES NOT WORK
                break;
            case "noticeBooker":
                c.render = (text, record: TableType) => renderContinuousText(text, 200, undefined, options?.filter?.find((filter) => filter.column === c.dataIndex)?.values[0]);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                c.sorter = false;
                break;
            case "credit":
                c.title = i18n.views.TableColumnTitles.bookerCredit();
                c.render = (text, record: TableType) => renderAmount(record.attendee?.booker?.credit ?? 0);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "number",
                });
                c.sorter = false;
                break;
            case "instructors":
                c.render = (text, record: TableType) => renderInstructors(record.course?.instructors ?? []);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                c.sorter = false;
                break;
            case "lessonCount":
                c.render = (text, record: TableType) => renderTextStandard(record.course?.lessonCount);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: true,
                    type: "number",
                });
                c.sorter = false;
                break;
            case "canceled":
                c.render = (text, record: TableType) => renderDate(record.canceled, false);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: true,
                    type: "date",
                });
                c.sorter = true;
                break;
            case "paymentMethod":
                c.render = (text, record: TableType) => {
                    // Bei Mollie-Buchungen zeigen wir die zuletzt erfasste Mollie-Methode
                    // (z.B. "Mollie: Kreditkarte") statt nur der Default-PM der Buchung.
                    // Dafür suchen wir den neuesten ScheduledPayment mit Mollie-Provider
                    // und gesetzter `mollieMethod` — falls keiner existiert, fällt die
                    // Darstellung auf den klassischen Badge zurück.
                    const scheduledPayments = record.scheduledPayments ?? [];
                    const mollieSp = [...scheduledPayments].reverse().find((sp) => sp.paymentProvider === PaymentProvider.Mollie && Boolean(sp.mollieMethod));

                    const isMollie = Boolean(mollieSp);

                    return (
                        <PaymentMethodBadge
                            type={record.paymentMethod ? getDisplayedPaymentMethodType(record.paymentMethod) : undefined}
                            paymentProvider={isMollie ? "Mollie" : undefined}
                            mollieMethod={isMollie && mollieSp ? mollieSp.mollieMethod : undefined}
                        />
                    );
                };
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "selectMultiple",
                    selectOptions: featureMollie
                        ? [
                              {label: "Überweisung (eigene Abwicklung)", value: PaymentMethodFilterCategory.BankTransferManual},
                              {label: "Überweisung (Mollie)", value: PaymentMethodFilterCategory.BankTransferMollie},
                              {label: "Lastschrift (eigene Abwicklung)", value: PaymentMethodFilterCategory.SepaDebitManual},
                              {label: "Lastschrift (Mollie)", value: PaymentMethodFilterCategory.SepaDebitMollie},
                              {label: "Bar", value: PaymentMethodFilterCategory.Cash},
                              {label: "Kreditkarte", value: PaymentMethodFilterCategory.Creditcard},
                              {label: "PayPal", value: PaymentMethodFilterCategory.Paypal},
                              {label: "Apple Pay", value: PaymentMethodFilterCategory.Applepay},
                              {label: "Google Pay", value: PaymentMethodFilterCategory.Googlepay},
                              {label: "Online-Zahlung (offen)", value: PaymentMethodFilterCategory.OnlineOpen},
                          ]
                        : [
                              {label: "Überweisung", value: PaymentMethodFilterCategory.BankTransferManual},
                              {label: "Lastschrift", value: PaymentMethodFilterCategory.SepaDebitManual},
                              {label: "Bar", value: PaymentMethodFilterCategory.Cash},
                          ],
                });
                c.sorter = false;
                break;
            case "discountCode":
                c.render = (text, record: TableType) => (record.discountCodeText ? <Typography.Text code>{record.discountCodeText}</Typography.Text> : null);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "selectMultiple",
                    selectOptions: [
                        ...(discountCodeOptionsData?.discountCodes ?? []).map((discountCode) => ({label: discountCode.code, value: discountCode.code})),
                        {label: DISCOUNT_CODE_FILTER_NONE, value: DISCOUNT_CODE_FILTER_NONE},
                    ],
                });
                c.sorter = false;
                break;
            case "additionalInfo":
                c.render = (text, record: TableType) =>
                    renderAdditionalInfo({
                        additionalInfo: record.additionalInfo,
                    });
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    // type: "search",
                    type: "select",
                    selectOptions: [
                        {
                            label: "Mit Zusatzinfo",
                            value: "true",
                        },
                        {
                            label: "Ohne Zusatzinfo",
                            value: "false",
                        },
                    ],
                });
                break;
            case "bookedLessons":
                c.width = 150;
                c.render = (text, record: TableType, rowIndex: number) => ({
                    displayName: "BookedLessons",
                    children: (
                        <RenderBookedLessons
                            bookingId={record.id}
                            courseLessons={record.courseLessons as Array<CourseLesson>}
                            // allowLessonBooking={record.course?.allowLessonBooking ?? false}
                            allowLessonBooking={Boolean(record.course?.allowLessonBooking)}
                            indexInList={rowIndex}
                            courseLessonBookings={record.courseLessonBookings} // OPTIMIERUNG: Daten direkt übergeben
                        />
                    ),
                });
                sortFilter({
                    dataIndex: c.dataIndex,
                    // disabled: true,
                    type: "select",
                    selectOptions: [
                        {
                            label: "Einzeln buchbar",
                            value: "lessonBookingAllowed",
                        },
                        {
                            label: "Nicht einzeln buchbar",
                            value: "lessonBookingNotAllowed",
                        },
                    ],
                });
                // c.sorter = false;
                break;
            case "attendeeMember":
                c.width = 120;
                c.align = "center";
                c.render = (text, record: TableType) => renderBooleanAsCheckmark(Boolean(record.attendee?.member));
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "select",
                    selectOptions: [
                        {
                            label: "Mitglied",
                            value: "true",
                        },
                        {
                            label: "Nicht Mitglied",
                            value: "false",
                        },
                    ],
                });
                c.sorter = true;
                break;
            default:
                c.fixed = false;
                c.render = (text) => renderUnknown(text);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: true,
                    type: "search",
                });
                c.sorter = false;
                break;
        }
        // @ts-ignore
        columns.push(c);
    });

    // getColumnsToShow(columnsWithDataIndex);
    getColumnsToShow(columns);

    return shownColumns;
};

// ACTION BUTTONS---------------------------------------------------------------------------

const renderActions = ({record, editLink, detailsLink, actionColumn, selectedRowKeys, refetch, highestRole, roles, transferWizardMode, bookingActionsDisabled}: RenderActionsProps) => {
    // const {id: bookingId} = record;
    // const bookingId = record.id;
    // const isInstructor = highestRole === Roles.Instructor;
    // const lessonBookingDetailsLink = URL_LESSON_BOOKING_EDIT_ID.replace(":id", bookingId);
    // const settledLessonBookingDetailsLink = URL_LESSON_BOOKINGS_SETTLED_DETAILS.replace(":id", bookingId);
    // const regularBookingDetailsLink = isInstructor ? URL_INSTRUCTOR_BOOKING_EDIT_ID.replace(":id", bookingId) : URL_BOOKING_EDIT_ID.replace(":id", bookingId);
    // const regularSettledBookingDetailsLink = isInstructor ? URL_INSTRUCTOR_INVOICE_SETTLEMENT_DETAILS.replace(":id", bookingId) : URL_BOOKINGS_SETTLEMENT_DETAILS.replace(":id", bookingId);

    const bookingDetailsLink = () => {
        const bookingId = record.id;
        const allowLessonBooking = record.course?.allowLessonBooking;
        const isSettled = isBookingSettled(record);
        const isInstructor = highestRole === Roles.Instructor;
        const lessonBookingDetailsLink = URL_LESSON_BOOKING_EDIT_ID.replace(":id", bookingId);
        const settledLessonBookingDetailsLink = URL_LESSON_BOOKINGS_SETTLED_DETAILS.replace(":id", bookingId);
        const regularBookingDetailsLink = isInstructor ? URL_INSTRUCTOR_BOOKING_EDIT_ID.replace(":id", bookingId) : URL_BOOKING_EDIT_ID.replace(":id", bookingId);
        const regularSettledBookingDetailsLink = isInstructor ? URL_INSTRUCTOR_INVOICE_SETTLEMENT_DETAILS.replace(":id", bookingId) : URL_BOOKINGS_SETTLEMENT_DETAILS.replace(":id", bookingId);

        if (allowLessonBooking) {
            if (isSettled) {
                return settledLessonBookingDetailsLink;
            }

            return lessonBookingDetailsLink;
        }
        if (isSettled) {
            return regularSettledBookingDetailsLink;
        }

        return regularBookingDetailsLink;
    };

    return (
        <>
            <RenderActions
                record={record}
                // editLink={editLink}
                // detailsLink={detailsLink}
                editLink={bookingDetailsLink()}
                detailsLink={bookingDetailsLink()}
                actionColumn={actionColumn}
                selectedRowKeys={selectedRowKeys}
                refetch={refetch}
                waitListCountFromBooking={record.waitListCount ?? undefined}
                // #660, Teil 2c: bisher wurde `highestRole` zwar in die Spalte gereicht, aber nie
                // an `RenderActions` — deshalb sah jede Rolle „Umbuchen"/„Kopieren". Beides wird
                // jetzt hier durchgereicht und dort ausgewertet.
                highestRole={highestRole}
                roles={roles}
                transferWizardMode={transferWizardMode}
                bookingActionsDisabled={bookingActionsDisabled}
            />
        </>
    );
};
